<template>
    <form @submit.prevent="jsonpSubmit">
        <slot :fields="fields" :is-loading-submit="isLoadingSubmit" :errors="errors" :submit="jsonpSubmit"></slot>

        <popup :priority="110" size="large" :is-forced-visible="isLoadingRedirect">
            <p class="txt-center no-space">
                <button class="btn transparent large loading"></button>
            </p>
        </popup>
    </form>
</template>

<script>
    import axios from 'axios';
    import jsonp from 'axios-jsonp';
    import { postRedirect } from "../../helpers/yii";
    import {getAnalyticsClientId} from "../../helpers/analytics";

    export default {
        name: 'SignUpForm',

        props: {
            initialFields: Object,
            signUpUrl: { type: String, required: true },
            finishLoginUrl: { type: String, required: true },
            token: { type: String, required: true },
            language: { type: String, required: true },
            paymentOptionPlans: { type: Object, required: true },
        },

        data() {
            return {
                fields: {
                    email: this.initialFields.email || window.sessionStorage.email || '',
                    password: this.initialFields.password || '',
                    paymentOption: this.initialFields.paymentOption,
                    planType: this.initialFields.planType || 'recurring',
                    recurringJoinOption: this.initialFields.recurringJoinOption,
                    oneTimeJoinOption: this.initialFields.oneTimeJoinOption,
                    agree: (typeof this.initialFields.agree === "undefined") ? true : this.initialFields.agree || window.sessionStorage.agree,
                },
                errors: {},
                isLoadingSubmit: false,
                isLoadingRedirect: false,
            };
        },

        mounted() {
            // When hitting the back button on the biller page, the browser redirects back to the signup page
            // But on Safari, the Javascript state is persisted when navigating back
            // So we need to reset the loading icons manually
            // This is further explained here:
            // https://gomakethings.com/fixing-safaris-back-button-browser-cache-issue-with-vanilla-js/
            window.onpageshow = (event) => {
                if (event.persisted) {
                    this.isLoadingSubmit = false;
                    this.isLoadingRedirect = false;
                }
            };
        },

        watch: {
            'fields.email': function (email) {
                window.sessionStorage.email = email;
            },
            'fields.agree': function (agree) {
                window.sessionStorage.agree = agree;
            },
            'fields.paymentOption': function (paymentOption) {
                const availablePlans = this.paymentOptionPlans[paymentOption];
                if (!availablePlans) {
                    // Could not find the available plans for the payment option - probably something went wrong; do nothing
                    return;
                }

                if (availablePlans.indexOf(this.fields.planType) !== -1) {
                    // The selected plan type is available; do nothing
                    return;
                }

                // The selected plan is not available for the given payment option, so we change the plan
                this.fields.planType = availablePlans[0];
            },
            'fields.planType': function (planType) {
                const availablePlans = this.paymentOptionPlans[this.fields.paymentOption];
                if (!availablePlans) {
                    // Could not find the available plans for the payment option - probably something went wrong; do nothing
                    return;
                }

                if (availablePlans.indexOf(planType) !== -1) {
                    // The selected plan type is available; do nothing
                    return;
                }

                // The selected plan is not available for the given payment option, so we change the payment option
                const viableOption = Object.entries(this.paymentOptionPlans).find(([option, plans]) => plans.indexOf(planType) !== -1) || [];
                this.fields.paymentOption = viableOption[0] || null;
            },
        },

        methods: {
            async jsonpSubmit() {
                if (this.isLoadingSubmit) {
                    return;
                }

                const joinOption = this.fields.planType === 'recurring' ? this.fields.recurringJoinOption : this.fields.oneTimeJoinOption;

                if (typeof window.gtag !== 'undefined') {
                    gtag('event', 'sign_up', {
                        method: 'Paid',
                        value: joinOption,
                    });
                } else if (typeof window.ga !== 'undefined') {
                    ga('send', 'event', 'engagement', 'sign_up', 'Paid', joinOption);
                }

                this.isLoadingSubmit = true;
                try {
                    const response = await axios({
                        url: this.signUpUrl,
                        adapter: jsonp,
                        params: {
                            email: this.fields.email,
                            password: this.fields.password,
                            payment_option: this.fields.paymentOption,
                            join_option: joinOption,
                            agree: this.fields.agree,
                            language: this.language,
                            token: this.token,
                            analytics_client_id: await getAnalyticsClientId(),
                        },
                    })

                    if (response.data.hasOwnProperty('errors')) {
                        this.errors = response.data.errors;
                        return;
                    }

                    this.errors = {};
                    this.isLoadingRedirect = true;
                    if (response.data.payload.data) {
                        postRedirect(response.data.payload.url, response.data.payload.data);
                    } else if (response.data.payload.url) {
                        window.location = response.data.payload.url;
                    } else if (response.data.payload.loginToken) {
                        window.location = this.finishLoginUrl + '?login_token=' + response.data.payload.loginToken;
                    } else {
                        window.location.reload(true);
                    }
                } finally {
                    this.isLoadingSubmit = false;
                }
            }
        },
    }
</script>
